// src/components/admin/feedback/FeedbackAuditLogs.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Edit,
  Add,
  Delete,
  Visibility
} from '@mui/icons-material';
import auditLogService from '../../../services/auditLogService';

const FeedbackAuditLogs = ({ feedbackId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    if (feedbackId) {
      loadAuditLogs();
    }
  }, [feedbackId]);

  const loadAuditLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditLogService.getAuditLogsByFeedback(feedbackId);
      setLogs(response.data.content || response.data || []);
    } catch (err) {
      setError(err.message || 'Грешка при зареждане на логове');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE':
        return <Add fontSize="small" />;
      case 'UPDATE':
        return <Edit fontSize="small" />;
      case 'DELETE':
        return <Delete fontSize="small" />;
      default:
        return null;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('bg-BG'),
      time: date.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Alert severity="info">
        Няма налични логове за този сигнал. Това означава, че все още не са направени промени на база този сигнал.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        История на промените от този сигнал
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Всички промени направени в системата на база този feedback сигнал
      </Typography>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="50px"></TableCell>
              <TableCell>Действие</TableCell>
              <TableCell>Обект</TableCell>
              <TableCell>Читалище</TableCell>
              <TableCell>Изпълнено от</TableCell>
              <TableCell>Дата</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => {
              const performedDate = formatDate(log.performedAt);
              const isExpanded = expandedRows[log.id];

              return (
                <React.Fragment key={log.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(log.id)}>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getActionIcon(log.action)}
                        label={log.action}
                        size="small"
                        color={getActionColor(log.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.entityType === 'CHITALISHTE' ? 'Читалище' : 'Информационна карта'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Рег. № ${log.chitalishteRegistrationNumber || 'N/A'}`}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {log.chitalishteName || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.performedBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2">{performedDate.date}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {performedDate.time}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Описание:
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {log.description}
                          </Typography>

                          {log.changedFields && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Променени полета:
                              </Typography>
                              <Typography variant="body2" paragraph>
                                {log.changedFields}
                              </Typography>
                            </>
                          )}

                          {log.oldValues && log.action === 'UPDATE' && (
                            <Stack direction="row" spacing={2}>
                              <Box flex={1}>
                                <Typography variant="caption" color="text.secondary">
                                  Стари стойности (JSON):
                                </Typography>
                                <Paper 
                                  variant="outlined" 
                                  sx={{ p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'white' }}
                                >
                                  <Typography 
                                    variant="caption" 
                                    component="pre" 
                                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                                  >
                                    {JSON.stringify(JSON.parse(log.oldValues), null, 2)}
                                  </Typography>
                                </Paper>
                              </Box>
                              <Box flex={1}>
                                <Typography variant="caption" color="text.secondary">
                                  Нови стойности (JSON):
                                </Typography>
                                <Paper 
                                  variant="outlined" 
                                  sx={{ p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'white' }}
                                >
                                  <Typography 
                                    variant="caption" 
                                    component="pre" 
                                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                                  >
                                    {JSON.stringify(JSON.parse(log.newValues), null, 2)}
                                  </Typography>
                                </Paper>
                              </Box>
                            </Stack>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default FeedbackAuditLogs;